/**
 * IX-2646: Post-conversion form-submit activation (inline AJAX confirmation path).
 *
 * ⚠️ NOT YET TESTED IN PRODUCTION. This feature ships without automated test
 * coverage and has not been validated end-to-end on a live client. First
 * production trial is on Matera — verify manually (DevTools + PostHog
 * `rw_client_form_submitted` → `rw_widget_impression[isPostConversion=true]`)
 * before relying on it for a new client. Add unit/integration tests before
 * broad rollout.
 */

import type { FormTrackingData } from '../analytics/formDetection/types';
import type { QualificationProfilingFormsValue } from '../types/configs.generated';
import { logger } from '../utils/core/logger';
import { matchesUrlPattern } from '../utils/web/urlUtils';
import { getResolverForDomain, matchPostConversionUrlPattern } from './SiteConfigManager';

export const FORM_SUBMIT_ACTIVATION_EVENT = 'rose_pc_form_submit_activated';
export const CLIENT_API_ACTIVATION_EVENT = 'rose_pc_client_api_activated';

export interface ClientPostConversionActivationOptions {
    formId?: string;
    context?: Record<string, string>;
}

export interface ClientPostConversionActivation {
    source: 'client_api';
    formId?: string;
    context?: Record<string, string>;
    activatedAt: number;
}

interface ClientApiPostConversionFormMatch {
    formId: string;
    config: QualificationProfilingFormsValue;
}

function storageKey(domain: string): string {
    return `rose_pc_form_submit_activated_${domain}`;
}

function clientApiStorageKey(domain: string): string {
    return `rose_pc_client_api_activated_${domain}`;
}

function matchFormActionPattern(value: string, pattern: string): boolean {
    if (!pattern?.trim()) return false;
    return matchesUrlPattern(value, pattern);
}

function hasRoseDemoParam(): boolean {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).has('rose_demo');
}

function matchesCurrentPage(formConfig: QualificationProfilingFormsValue): boolean {
    if (typeof window === 'undefined') return false;

    const currentUrl = window.location.origin + window.location.pathname;
    const currentPathname = window.location.pathname;
    const pages = formConfig.trigger?.pages ?? [];
    return pages.some(
        (pattern: string) =>
            matchPostConversionUrlPattern(currentUrl, pattern) ||
            matchPostConversionUrlPattern(currentPathname, pattern)
    );
}

function resolveClientApiPostConversionForm(
    domain: string,
    requestedFormId?: string
): ClientApiPostConversionFormMatch | null {
    const profiling = getResolverForDomain(domain)?.qualification?.profiling;
    if (!profiling?.enabled || !profiling?.forms) {
        return null;
    }

    const candidates: ClientApiPostConversionFormMatch[] = [];
    const forms = profiling.forms as Record<string, QualificationProfilingFormsValue>;
    const hasPreviewAccess = hasRoseDemoParam();
    for (const [formId, formConfig] of Object.entries(forms)) {
        if (requestedFormId && formId !== requestedFormId) continue;
        if (formConfig?.trigger?.type !== 'post_conversion') continue;

        // Preview defaults to true for post-conversion forms. Keep that safety
        // gate for the public API too: preview forms are testable with ?rose_demo.
        if (formConfig.preview !== false && !hasPreviewAccess) continue;

        candidates.push({ formId, config: formConfig });
    }

    return (
        candidates.find((candidate) => matchesCurrentPage(candidate.config)) ??
        candidates[0] ??
        null
    );
}

/**
 * Called from the form detection pipeline whenever a client form is submitted.
 * If the submission matches a post-conversion form's on_form_submit trigger
 * (page + form_action pattern), we store the formId in sessionStorage and
 * dispatch a window event so DisplayController re-evaluates its decision.
 */
export function notifyFormSubmit(domain: string, data: FormTrackingData): void {
    if (typeof window === 'undefined') return;
    if (data.status !== 'success') return;

    const profiling = getResolverForDomain(domain)?.qualification?.profiling;
    if (!profiling?.enabled || !profiling?.forms) return;

    const currentUrl = window.location.origin + window.location.pathname;
    const currentPathname = window.location.pathname;
    const formAction = data.formAction ?? '';

    const forms = profiling.forms as Record<string, QualificationProfilingFormsValue>;
    for (const [formId, formConfig] of Object.entries(forms)) {
        const onSubmit = formConfig?.trigger?.on_form_submit;
        if (!onSubmit?.enabled) continue;

        const pages = onSubmit.pages ?? [];
        const pageMatches = pages.some(
            (pattern: string) =>
                matchPostConversionUrlPattern(currentUrl, pattern) ||
                matchPostConversionUrlPattern(currentPathname, pattern)
        );
        if (!pageMatches) continue;

        const actionPatterns = onSubmit.form_action_matches ?? [];
        if (actionPatterns.length === 0) {
            logger.debug(
                '⏭️ on_form_submit: skipping — form_action_matches must be non-empty (safety gate)',
                { formId }
            );
            continue;
        }

        const actionMatches = actionPatterns.some((pattern: string) =>
            matchFormActionPattern(formAction, pattern)
        );
        if (!actionMatches) {
            logger.debug('⏭️ on_form_submit: form_action did not match any configured pattern', {
                formId,
                formAction,
                patterns: actionPatterns,
            });
            continue;
        }

        let persisted = false;
        try {
            window.sessionStorage.setItem(storageKey(domain), formId);
            persisted = true;
        } catch (err) {
            logger.warn('Failed to persist post-conversion form-submit activation', err);
        }

        // Without a successful write, isFormSubmitActivated() will return false and
        // the listener becomes a silent no-op. Skip the dispatch so the debug log
        // below doesn't misreport activation.
        if (!persisted) return;

        logger.debug('🎯 on_form_submit: activation triggered by form submission', {
            formId,
            currentUrl,
            formAction,
        });

        window.dispatchEvent(
            new CustomEvent(FORM_SUBMIT_ACTIVATION_EVENT, {
                detail: { domain, formId },
            })
        );
        return;
    }
}

/**
 * Activation is sessionStorage-scoped: it survives SPA navigation and refreshes
 * within the same tab, and naturally clears on tab close. Re-opening the
 * qualification on page revisits within the session is intended (nudge the
 * visitor back to finish), and RoseWidget's auto-open logic additionally
 * respects two higher-level flags before displaying:
 *   - `completedPostConversion` (localStorage) — qualification finished
 *   - `rose_pcq_dismissed_{domain}` (sessionStorage) — user dismissed
 * so revisits after completion or explicit dismissal will not re-prompt.
 */
export function isFormSubmitActivated(domain: string, formId: string): boolean {
    if (typeof window === 'undefined') return false;
    try {
        return window.sessionStorage.getItem(storageKey(domain)) === formId;
    } catch {
        return false;
    }
}

export function activatePostConversionFromClientApi(
    domain: string,
    options: ClientPostConversionActivationOptions = {}
): boolean {
    if (typeof window === 'undefined') return false;
    if (!domain?.trim()) {
        logger.warn('Client API post-conversion activation skipped: missing domain');
        return false;
    }

    const requestedFormId = options.formId?.trim();
    const formMatch = resolveClientApiPostConversionForm(domain, requestedFormId);
    if (!formMatch) {
        logger.warn('Client API post-conversion activation skipped: no active form found', {
            domain,
            formId: requestedFormId,
            hint: 'Post-conversion forms in preview require ?rose_demo=1; live forms need preview=false.',
        });
        return false;
    }

    const activation: ClientPostConversionActivation = {
        source: 'client_api',
        formId: formMatch.formId,
        activatedAt: Date.now(),
    };
    if (options.context && Object.keys(options.context).length > 0) {
        activation.context = options.context;
    }

    try {
        window.sessionStorage.setItem(clientApiStorageKey(domain), JSON.stringify(activation));
        window.sessionStorage.removeItem(`rose_pcq_dismissed_${domain}`);
    } catch (err) {
        logger.warn('Failed to persist client API post-conversion activation', err);
        return false;
    }

    logger.debug('🎯 Client API post-conversion activation triggered', {
        domain,
        formId: activation.formId,
        hasContext: !!activation.context,
    });

    window.dispatchEvent(
        new CustomEvent(CLIENT_API_ACTIVATION_EVENT, {
            detail: { domain, formId: activation.formId },
        })
    );
    return true;
}

export function getClientPostConversionActivation(
    domain: string
): ClientPostConversionActivation | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.sessionStorage.getItem(clientApiStorageKey(domain));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<ClientPostConversionActivation>;
        if (parsed.source !== 'client_api' || typeof parsed.activatedAt !== 'number') {
            return null;
        }
        const activation: ClientPostConversionActivation = {
            source: 'client_api',
            activatedAt: parsed.activatedAt,
        };
        if (typeof parsed.formId === 'string' && parsed.formId.trim()) {
            activation.formId = parsed.formId.trim();
        }
        if (parsed.context && typeof parsed.context === 'object') {
            const context: Record<string, string> = {};
            for (const [key, value] of Object.entries(parsed.context)) {
                if (typeof value === 'string') {
                    context[key] = value;
                }
            }
            if (Object.keys(context).length > 0) {
                activation.context = context;
            }
        }
        return activation;
    } catch {
        return null;
    }
}
